import { Fragment, useState } from "react";
import { useInView } from "react-intersection-observer";
import { ArticleDataObject, Tag } from "@app/types/Cue";
import { AdvertisementTypeEnum } from "@app/types/enums";
import { ArticleContext } from "@app/types/Page";
import Advertisement from "@components/Advertisement/Advertisement";
import PrestitialAdWrapper from "@components/Advertisement/PrestitialAdWrapper";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import Container from "@components/Container/Container";
import FooterSubscribeRow from "@components/Footer/FooterSubscribeRow/FooterSubscribeRow";
import GAData from "@components/GAData/GAData";
import {
  getArticleAuthorNames,
  getArticleChapter1,
  getArticleKeywords,
  getArticleLevel2,
} from "@components/GAData/helper";
import Header from "@components/Header/Header";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useArticleGiftReceived from "@hooks/useArticleGiftReceived";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import ArticleSubshareModal from "@pages/Article/components/ArticleSubshare/ArticleSubshareModal";
import ArticleSubshareReceived from "@pages/Article/components/ArticleSubshare/ArticleSubshareReceived";
import {
  ArticleFactory,
  VerticalHeaderFactory,
} from "@pages/Article/utils/helpers";
import { getFormattedTextForAds } from "@pages/Section/helpers";
import { CustomContext, useRouteContext } from "@sphtech/web2-core/ssr";
import {
  ARTICLE_INIFINITE_SCROLL_LIMIT,
  sectionNavigationItems,
} from "@util/constant";
import {
  GoogleAdsSlotFactory,
  isPathAVerticalPath,
  toUnderscoreFromSpace,
} from "@util/helpers";
import axios from "axios";

import ArticleGrapeshot, {
  GrapeshotDisplayOptions,
} from "./components/ArticleGrapeshot";
import ArticleSubsharePrompt from "./components/ArticleSubshare/ArticleSubsharePrompt";
import { LAMBDA_RESPONSE_TYPES } from "./types/Alacrity";

export default function ArticlePage(): React.ReactElement {
  const { context }: CustomContext<ArticleContext> = useRouteContext();

  const articleContext = context.data?.cook?.data?.context;
  const breakingNews = context?.data?.breakingNews ?? [];
  const sectionNews = context?.data?.sectionNews ?? [];

  const [moreStories, setMoreStories] = useState<ArticleDataObject[]>([
    articleContext,
  ]);
  const [isLoading, setIsLoading] = useState(false);

  const mainSection = articleContext?.sections?.at(0);
  const uniqueName = mainSection?.uniqueName;
  const slug = articleContext?.urlPath;

  const parentCategory = uniqueName?.split("_")[0];
  const childCategory = uniqueName?.split("_")[1];
  const { isPrestitialEnabled, topOverlayImpressions } = useGlobalAdSetting({
    targeting: [
      { key: "page", value: "article" },
      {
        key: "bttags",
        value:
          articleContext?.tags
            ?.map((tag: Tag) => getFormattedTextForAds(tag.name))
            .join(", ") || "",
      },
    ],
  });

  const uniqueNameAds = uniqueName?.replace("_", "/") || "";
  const verticalPath = "/" + uniqueNameAds;
  const VerticalHeaderLayout = VerticalHeaderFactory({ verticalPath });

  const { isModalOpen, setIsModalOpen, alacrityRes, isGiftReceived } =
    useArticleGiftReceived();
  const [showGiftPrompt, setShowGiftPrompt] = useState(false);

  const isThisArticlegifted =
    alacrityRes?.data.responseType ===
    LAMBDA_RESPONSE_TYPES.SUCCESS_VALID_GIFT_TOKEN;

  /**
   * FOR INFINITE SCROLL
   */
  const fetchTheNextArticle = async () => {
    setIsLoading(true);
    const lastArticle = moreStories?.at(-1);
    if (!lastArticle) return;
    console.log("[START] Fetching next article..."); // eslint-disable-line no-console
    console.log({ moreStories, lastArticle }); // eslint-disable-line no-console

    try {
      const response = await axios.post("/_plat/api/v1/next-article", {
        category: lastArticle?.sections?.[0].uniqueName, // "companies-markets_banking-finance" || "food-drink"
        sort: lastArticle?.sort, // [1703168467000, "2166527"]
      });
      const nextStory = response.data;
      setMoreStories([...moreStories, nextStory]);
    } catch (error) {
      // return [];
    } finally {
      setIsLoading(false);
    }

    console.log("[END] Fetching next article..."); // eslint-disable-line no-console
  };

  const { ref } = useInView({
    triggerOnce: true,
    onChange: (inView) => {
      if (
        inView &&
        moreStories.length < ARTICLE_INIFINITE_SCROLL_LIMIT &&
        true // isLocalDevOrStg CONSTANT used in bt-web
      )
        fetchTheNextArticle();
    },
  });

  const { ref: showGiftPromptRef } = useInView({
    triggerOnce: true,
    onChange: (inView) => {
      if (inView && isThisArticlegifted) setShowGiftPrompt(true);
    },
  });

  return (
    <>
      {!isPathAVerticalPath(verticalPath) ? (
        <Advertisement
          rootClassName="bg-stripes py-3"
          adUnitProps={{
            type: AdvertisementTypeEnum.LB1,
            slot: GoogleAdsSlotFactory.lb1(uniqueNameAds),
          }}
        />
      ) : null}

      <PrestitialAdWrapper
        slot={GoogleAdsSlotFactory.prestitial(uniqueNameAds)}
        isPrestitialEnabled={isPrestitialEnabled}
        topOverlayImpressions={topOverlayImpressions}
      />

      <Header mastheadTitle={uniqueName} />

      {parentCategory ? (
        <div className="w-full flex-none bg-gray-250">
          <Container>
            <Breadcrumb
              rootClassName="m-auto"
              parentCategory={parentCategory}
              childCategory={childCategory}
              categoryDataMapping={sectionNavigationItems}
            />
          </Container>
        </div>
      ) : null}

      <>
        {VerticalHeaderLayout ? <VerticalHeaderLayout /> : null}

        <div id="article-content-wrapper" className="w-full">
          {moreStories.map((article, index) => {
            // Check if article is undefined
            if (!article) {
              return null;
            }

            const displayType = article.displaySetting?.displayType;
            const mainSection = article.sections?.at(0);
            const ArticleLayout = ArticleFactory({ displayType });
            const isPremium = article.paywall?.contentAccess === "1";

            if (!displayType || !ArticleLayout) {
              return null;
            }

            return (
              <Fragment key={article.id}>
                <>
                  {slug ? (
                    <ArticleGrapeshot
                      article_url={slug}
                      displayOptions={GrapeshotDisplayOptions.Meta}
                    />
                  ) : null}
                  {mainSection ? (
                    <GAData
                      articleid={article.id}
                      author={getArticleAuthorNames(article.authors)}
                      chapter1={getArticleChapter1(mainSection)}
                      contentcat={isPremium ? 2 : 1}
                      contenttype="1"
                      keyword={getArticleKeywords(article.tags || [])}
                      level2={getArticleLevel2(mainSection)}
                      pubdate={article.updated}
                      title={toUnderscoreFromSpace(article.title)}
                    />
                  ) : null}
                  {slug ? (
                    <ArticleGrapeshot
                      article_url={slug}
                      displayOptions={GrapeshotDisplayOptions.GTM}
                    />
                  ) : null}
                </>

                <ArticleLayout
                  article={article}
                  isGiftReceived={isGiftReceived}
                  breakingNews={breakingNews}
                  sectionNews={sectionNews}
                  variant="default"
                  index={index}
                  seeAlso={context.data.cook.data.seeAlso}
                />

                <div ref={ref}></div>
              </Fragment>
            );
          })}
        </div>

        <ArticleSubsharePrompt
          isModalOpen={showGiftPrompt}
          setIsModalOpen={setShowGiftPrompt}
        />

        <div
          aria-roledescription="article-subshare-prompt-observer"
          ref={showGiftPromptRef}
        ></div>

        {isLoading ? (
          <FontAwesomeIcon icon={faSpinner} size="5x" spin className="mb-6" />
        ) : null}

        <FooterSubscribeRow />

        <>
          {alacrityRes ? (
            <ArticleSubshareModal
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
            >
              <ArticleSubshareReceived
                alacrityRes={alacrityRes}
                setIsModalOpen={setIsModalOpen}
              />
            </ArticleSubshareModal>
          ) : null}
        </>
      </>
    </>
  );
}
